// CourseProgressOverlay.jsx

import React, {useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated,
} from 'react-native-web';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useDispatch, useSelector} from 'react-redux';
import {useEffect} from 'react';
import {BASE_URL, UNIT_TRACKING_API, API_KEY} from '@env';

// Blinking Icon component remains unchanged
const BlinkingIcon = () => {
  const blinkAnim = useRef(new Animated.Value(1)).current;
  React.useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(blinkAnim, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(blinkAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ]),
    ).start();
  }, [blinkAnim]);
  return (
    <Animated.View style={{opacity: blinkAnim}}>
      <Icon name="circle" size={10} color="#FFF" style={{marginRight: 4}} />
    </Animated.View>
  );
};

// Helper to format course title as multiline (aligned right)
const formatCourseTitle = title => {
  const regex = /^(.*?)(\s*\(.*\))$/;
  const match = title.match(regex);
  if (match) {
    return (
      <Text style={styles.courseTitle}>
        {`${match[1].trim()}\n${match[2].trim()}`}
      </Text>
    );
  }
  return <Text style={styles.courseTitle}>{title}</Text>;
};

const CourseProgressOverlay = () => {
  const dispatch = useDispatch();
  const {courseData, loading, error} = useSelector(state => state.course);
  const [unitTrackingLogged, setUnitTrackingLogged] = useState(false);

  // Local UI states for the overlay
  const [expandedId, setExpandedId] = useState(null);
  const [hoveredUnitId, setHoveredUnitId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({top: 0, left: 0});
  const ready_to_move = useSelector(
    state => state.meeting.meeting.ready_to_move,
  );
  const token = useSelector(state => state.meeting.properties.token);

  const [isHovered, setIsHovered] = useState(true);

  async function TrackUnit() {
    try {
      const requestBody = {
        unitId: courseData.units[courseData.currentUnitIndex].id,
      };

      const endpoint = UNIT_TRACKING_API;

      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      console.log('Unit Tracking updated successfully:', result);
    } catch (error) {
      console.error('Error Tracking Unit progress:', error);
    }
  }

  useEffect(() => {
    if (!loading && courseData && !unitTrackingLogged && ready_to_move) {
      console.log('Hitting Unit Trackng API');
      TrackUnit();
      setUnitTrackingLogged(true);
    }
  }, [loading, courseData, ready_to_move]);

  if (!ready_to_move) return;

  if (loading) {
    return (
      <View
        style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Text style={{color: '#FFF', padding: 12}}>Loading...</Text>
      </View>
    );
  }

  if (error || !courseData) {
    return (
      <View
        style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Text style={{color: '#FFF', padding: 12}}>{error}</Text>
      </View>
    );
  }

  const {courseTitle, units, courseMastery} = courseData || {
    courseTitle: 'Course Title Placeholder',
    units: [],
    courseMastery: 0,
  };

  const toggleUnit = unitId => {
    setExpandedId(prev => (prev === unitId ? null : unitId));
  };

  const getStatusBadge = unit => {
    const renderMasteryBadge = () => {
      if (unit.unitMasteryPercentage >= 95) {
        return (
          <View
            style={[styles.badge, {backgroundColor: '#f59e0b', marginLeft: 4}]}>
            <Text style={styles.badgeText}>
              Expert {unit.unitMasteryPercentage}%
            </Text>
          </View>
        );
      } else if (unit.unitMasteryPercentage >= 90) {
        return (
          <View
            style={[styles.badge, {backgroundColor: '#065f46', marginLeft: 4}]}>
            <Text style={styles.badgeText}>
              Mastered {unit.unitMasteryPercentage}%
            </Text>
          </View>
        );
      } else if (
        unit.unitMasteryPercentage === 0 &&
        unit.lessonsCompletedPercentage === 0
      ) {
        return (
          <View
            style={[styles.badge, {backgroundColor: '#fca5a5', marginLeft: 4}]}>
            <Text style={styles.badgeText}>Not started</Text>
          </View>
        );
      }
      // Default % text
      return (
        <Text style={[styles.statusDefault, {marginLeft: 4}]}>
          {unit.unitMasteryPercentage}%
        </Text>
      );
    };

    if (unit.isCurrentlyStudying) {
      return (
        <View style={styles.badgeGroup}>
          <View
            style={[styles.badge, {backgroundColor: '#DA3F3F', marginLeft: 0}]}>
            <BlinkingIcon />
            <Text style={styles.badgeText}>Current</Text>
          </View>
          {renderMasteryBadge()}
        </View>
      );
    }

    return renderMasteryBadge();
  };

  const overallMastery = courseMastery;

  return (
    <View
      style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <View style={styles.overlay}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Course Progress</Text>
          <View style={{alignItems: 'flex-end'}}>
            {formatCourseTitle(courseTitle)}
            <View style={styles.courseProgressContainer}>
              <Text style={styles.progressLabel}>Course Mastery</Text>
              <Text style={styles.progressValue}>{overallMastery}%</Text>
            </View>
            <View style={styles.progressBarOuter}>
              <View
                style={[styles.progressBarInner, {width: `${overallMastery}%`}]}
              />
            </View>
          </View>
        </View>
        <ScrollView style={styles.unitsContainer}>
          {units.map(unit => {
            const isExpanded = unit.id === expandedId;
            return (
              <View key={unit.id} style={styles.unitWrapper}>
                <TouchableOpacity
                  activeOpacity={1}
                  style={[
                    styles.unitHeader,
                    hoveredRowId === unit.id && styles.unitHeaderHover,
                  ]}
                  onPress={() => toggleUnit(unit.id)}
                  onMouseEnter={() => setHoveredRowId(unit.id)}
                  onMouseLeave={() => setHoveredRowId(null)}>
                  <View style={styles.unitHeaderLeft}>
                    <Text style={styles.unitTitle}>
                      <Text style={{fontWeight: 'bold'}}>
                        Unit {unit.unitNumber}:{' '}
                      </Text>
                      {unit.title}
                    </Text>
                    <View
                      onMouseEnter={e => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const tooltipWidth = 220;
                        const tooltipLeft =
                          rect.left + rect.width / 2 - tooltipWidth / 2;
                        setTooltipPosition({
                          top: rect.bottom + 5,
                          left: tooltipLeft,
                        });
                        setHoveredUnitId(unit.id);
                      }}
                      onMouseLeave={() => setHoveredUnitId(null)}
                      style={{position: 'relative', marginHorizontal: 4}}>
                      <Icon
                        name="information-outline"
                        size={14}
                        color="#BDDE36"
                      />
                    </View>
                    {hoveredUnitId === unit.id &&
                      ReactDOM.createPortal(
                        <View
                          style={[
                            styles.tooltip,
                            {
                              top: tooltipPosition.top,
                              left: tooltipPosition.left,
                            },
                          ]}>
                          <View style={styles.tooltipRow}>
                            <Text style={styles.tooltipLabel}>
                              Total Lessons:
                            </Text>
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                              }}>
                              <Icon
                                name="book-outline"
                                size={14}
                                color="#BDDE36"
                              />
                              <Text
                                style={[styles.tooltipValue, {marginLeft: 4}]}>
                                {unit.totalLessons}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.tooltipRow}>
                            <Text style={styles.tooltipLabel}>
                              100% Completed:
                            </Text>
                            <View
                              style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                              }}>
                              <Icon
                                name="check-circle-outline"
                                size={14}
                                color="#BDDE36"
                              />
                              <Text
                                style={[styles.tooltipValue, {marginLeft: 4}]}>
                                {unit.hundredPercentCompletedCount}
                              </Text>
                            </View>
                          </View>
                        </View>,
                        document.body,
                      )}
                  </View>
                  <View style={styles.unitHeaderRight}>
                    {getStatusBadge(unit)}
                    <Icon
                      name={isExpanded ? 'chevron-up' : 'chevron-down'}
                      size={14}
                      color="#BDDE36"
                      style={{marginLeft: 8}}
                    />
                  </View>
                </TouchableOpacity>
                <View
                  style={[
                    styles.expandedContent,
                    isExpanded ? styles.expanded : styles.collapsed,
                  ]}>
                  <View style={styles.progressRow}>
                    <Text style={styles.progressRowLabel}>Unit Mastery</Text>
                    <Text style={styles.progressRowValue}>
                      {unit.unitMasteryPercentage}%
                    </Text>
                  </View>
                  <View style={styles.smallBarOuter}>
                    <View
                      style={[
                        styles.smallBarInner,
                        {width: `${unit.unitMasteryPercentage}%`},
                      ]}
                    />
                  </View>
                  <View style={styles.progressRow}>
                    <Text style={styles.progressRowLabel}>
                      Lessons Completed
                    </Text>
                    <Text style={styles.progressRowValue}>
                      {unit.lessonsCompletedPercentage}%
                    </Text>
                  </View>
                  <View style={styles.smallBarOuter}>
                    <View
                      style={[
                        styles.smallBarInner,
                        {width: `${unit.lessonsCompletedPercentage}%`},
                      ]}
                    />
                  </View>
                  <View style={styles.dummy} />
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
};

export default CourseProgressOverlay;

const styles = StyleSheet.create({
  /* Container & Overlay */
  container: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 50,
    minWidth: 400,
    borderRadius: 8,
    shadowColor: 'rgba(197,232,55,1)',
    shadowOffset: {width: 0, height: 0},
    shadowOpacity: 0.35,
    shadowRadius: 15,
    elevation: 20,
  },
  overlay: {
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderColor: 'rgba(189,222,54,0.3)',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  /* Header */
  header: {
    backgroundColor: 'rgba(189,222,54,0.1)',
    borderBottomColor: 'rgba(189,222,54,0.2)',
    borderBottomWidth: 1,
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontWeight: 'bold',
    color: '#BDDE36',
    fontSize: 14,
  },
  courseTitle: {
    color: '#BDDE36',
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'right',
  },
  courseProgressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    width: 130,
  },
  progressLabel: {
    fontSize: 9,
    color: '#a1a1a1',
  },
  progressValue: {
    fontSize: 9,
    color: '#BDDE36',
  },
  progressBarOuter: {
    width: 130,
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.3)',
    marginTop: 3,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
  },
  /* Units List */
  unitsContainer: {
    maxHeight: 360,
  },
  unitWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(189,222,54,0.2)',
  },
  unitHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    transition: 'background-color 0.3s ease',
    justifyContent: 'space-between',
  },
  unitHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  unitHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },

  unitHeaderHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
  unitTitle: {
    fontSize: 12,
    color: '#FFF',
    marginRight: 8,
    flexShrink: 1,
  },
  /* Expand/Collapse */
  expandedContent: {
    width: '90%',
    alignSelf: 'center',
    overflow: 'hidden',
    transition: 'max-height 0.5s linear',
  },
  expanded: {
    maxHeight: 500,
  },
  collapsed: {
    maxHeight: 0,
  },
  dummy: {
    marginBottom: 12,
  },
  /* Progress Rows */
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  progressRowLabel: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  progressRowValue: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  smallBarOuter: {
    height: 5,
    backgroundColor: '#333',
    borderRadius: 4,
    overflow: 'hidden',
    marginTop: 4,
  },
  smallBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
  },
  /* Badges */
  badgeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
  },

  badge: {
    borderRadius: 50,
    paddingHorizontal: 8,
    paddingVertical: 3,
    // marginLeft: 'auto',  // overridden inline
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeText: {
    color: '#FFF',
    fontSize: 12,
  },
  statusDefault: {
    fontSize: 12,
    color: '#BDDE36',
    marginLeft: 'auto',
  },
  /* Tooltip */
  tooltip: {
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0,0.9)',
    borderRadius: 8,
    padding: 8,
    width: 220,
    zIndex: 9999,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
  },
  tooltipText: {
    color: '#fff',
    fontSize: 12,
    marginBottom: 6,
    lineHeight: 16,
    fontWeight: '500',
  },
  tooltipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  tooltipLabel: {
    color: '#a1a1a1',
    fontSize: 11,
    fontWeight: '500',
  },
  tooltipValue: {
    color: '#fff',
    fontSize: 11,
    textAlign: 'right',
  },
  /* 3-dot Menu (mini modal) */
  menuContainer: {
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0,0.9)',
    borderRadius: 8,
    padding: 6,
    zIndex: 9999,
    boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
    width: 160,
    borderColor: 'rgba(189,222,54,0.3)',
    borderWidth: 1,
  },
  menuItem: {
    padding: 8,
    borderRadius: 8,
  },
  menuItemHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
  menuItemText: {
    color: '#FFF',
    fontSize: 13,
  },
  menuTrigger: {
    padding: 4,
    borderRadius: 4,
  },
  menuTriggerHover: {
    backgroundColor: 'rgba(189,222,54,0.1)',
  },
});
